import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import VisitModalContent from './VisitModalContent';
import toast from 'react-hot-toast';
import { logEvent } from '../../analytics';
import startSession from '../../utils/session/startSession';
import SessionPresenter from '../../presenters/SessionPresenter';
import NoteTypeSelector from './NoteTypeSelector';
import confirmVisitDiscard from './VisitDiscardConfirm';
import completeSession from '../../utils/session/completeSession';
import RecordingContext from './RecordingContext';

const { Text, Title } = Typography;

export default function QuickEntryModal({ open, onClose, patient = null }) {
  const { t } = useTranslation();
  const ctx = useContext(RecordingContext);
  const [session, setSession] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const [showNoteTypeSelector, setShowNoteTypeSelector] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(patient);
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    if (open) {
      ctx.resetAttachedDocuments();
      ctx.setSessionUuid(null);
      // Set the patient if provided
      if (patient) {
        setSelectedPatient(patient);
      }
    }
  }, [open, patient]);

  // Check if there's content to finalize (documents or notes)
  useEffect(() => {
    const hasDocuments = ctx.attachedDocuments && ctx.attachedDocuments.length > 0;
    const hasNotes = ctx.userNotes && ctx.userNotes.trim().length > 0;
    setHasContent(hasDocuments || hasNotes);
  }, [ctx.attachedDocuments, ctx.userNotes]);

  const handleClose = async () => {
    if (!showNoteTypeSelector) {
      // Use session from state or context
      const sessionUuid = session?.getUUID() || ctx.sessionUuid;
      const hasDocuments = ctx.attachedDocuments && ctx.attachedDocuments.length > 0;
      const hasNotes = ctx.userNotes && ctx.userNotes.trim().length > 0;

      if (sessionUuid || hasDocuments || hasNotes) {
        const confirmed = await confirmVisitDiscard({ 
          sessionUuid: sessionUuid, 
          t 
        });
        if (!confirmed) {
          return; // Don't close if user cancels
        }
      }
      
      setModalKey(prev => prev + 1); // Increment key to force remount
      setSession(null);
      setSelectedPatient(null);
      setHasContent(false);
      ctx.setSessionUuid(null);
      onClose();
    }
  };

  const handleFinalize = async () => {
    try {
      let currentSession = session;
      
      // Create session if it doesn't exist
      if (!currentSession) {
        let sessionUuid = ctx.sessionUuid;
        if (!sessionUuid) {
          sessionUuid = await startSession();
          if (!sessionUuid) throw new Error('Failed to create session');
        }
        currentSession = new SessionPresenter({ uuid: sessionUuid });
        setSession(currentSession);
        if (!ctx.sessionUuid) {
          ctx.setSessionUuid(sessionUuid);
        }
      }

      // Complete the session with patient and notes
      const patientUuid = selectedPatient?.getUUID() || null;
      const userNotes = ctx.userNotes && ctx.userNotes.trim().length > 0 ? ctx.userNotes.trim() : null;
      const success = await completeSession(
        currentSession.getUUID(),
        patientUuid,
        0, // No audio chunks for quick entry
        {}, // No problem stats for quick entry
        userNotes,
        'quick_entry' // Mode for quick entry
      );

      if (!success) {
        throw new Error('Failed to complete session');
      }

      logEvent('SessionRecorder', 'QuickEntry');
      setShowNoteTypeSelector(true);
    } catch (error) {
      console.error('Error finalizing session:', error);
      toast.error(t('common.errors.general'));
    }
  };

  const handleCloseNoteTypeSelector = () => {
    setShowNoteTypeSelector(false);
    setSession(null);
    setSelectedPatient(null);
    // Clear the session from context when closing
    ctx.setSessionUuid(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={t('quick_entry.title')}
        open={open && !showNoteTypeSelector}
        onCancel={handleClose}
        maskClosable={false}
        footer={
          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              onClick={handleFinalize}
              size="large"
              disabled={!hasContent}
            >
              {t('quick_entry.generate_note')}
            </Button>
          </div>
        }
        width={800}
      >
        <div style={{ fontSize: '13px', justifyContent: 'center', display: 'flex', marginBottom: 20 }}>
          <Text>{t('quick_entry.subtitle')}</Text>
        </div>
        <VisitModalContent
          initialPatient={selectedPatient}
          key={modalKey}
          sessionUuid={ctx.sessionUuid || session?.getUUID()}
          onPatientChange={setSelectedPatient}
          fromQuickEntry={true}
        />
      </Modal>

      {showNoteTypeSelector && session && (
        <NoteTypeSelector
          sessionUuid={session?.getUUID()}
          visible={showNoteTypeSelector}
          onClose={handleCloseNoteTypeSelector}
          patient={selectedPatient}
        />
      )}
    </>
  );
} 